import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Accordion,
  AccordionItem,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  usePwaInstall,
} from '@structural-climbing/ui'

// The iOS Share glyph: a square with an arrow coming out of the top. Shown inline in
// step 1 so members can match it against what Safari shows.
function ShareIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      width="13"
      height="15"
      viewBox="0 0 14 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <line x1="7" y1="1" x2="7" y2="10" />
      <polyline points="4,4 7,1 10,4" />
      <path d="M2 7v6a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V7" />
    </svg>
  )
}

// An arrow dropping into a tray. The button used to carry the Share glyph, which says
// "send this to someone" everywhere outside the iOS steps.
function DownloadIcon({ size = 15 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="8" y1="2" x2="8" y2="10" />
      <polyline points="5,7 8,10 11,7" />
      <path d="M2.5 11v1.5a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1V11" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <line x1="3" y1="3" x2="11" y2="11" />
      <line x1="11" y1="3" x2="3" y2="11" />
    </svg>
  )
}

// HeroUI's default indicator points sideways when collapsed, which reads as "this
// navigates somewhere" rather than "this expands". Point it down, and up once open.
function ChevronIcon({ isOpen }: { isOpen: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
      aria-hidden="true"
    >
      <polyline points="5,7 9,12 13,7" />
    </svg>
  )
}

// Every iOS browser is WebKit underneath and adds to the Home Screen through the same
// system share sheet, so the steps are identical. Only the browser name and where its
// Share button sits differ, and naming the wrong browser makes the whole modal read as
// broken. Brand names are deliberately not translated.
function iosBrowserName(userAgent: string): string {
  if (/CriOS/.test(userAgent)) return 'Chrome'
  if (/FxiOS/.test(userAgent)) return 'Firefox'
  if (/EdgiOS/.test(userAgent)) return 'Edge'
  if (/OPiOS|OPT\//.test(userAgent)) return 'Opera'
  return 'Safari'
}

// Android browsers each keep the install entry in their own menu, under their own label,
// so the steps name the browser for the same reason the iOS ones do.
function androidBrowserName(userAgent: string): string {
  if (/SamsungBrowser/.test(userAgent)) return 'Samsung Internet'
  if (/Firefox/.test(userAgent)) return 'Firefox'
  if (/EdgA/.test(userAgent)) return 'Edge'
  if (/OPR\//.test(userAgent)) return 'Opera'
  return 'Chrome'
}

// "Not now" on the banner hides it for a while rather than for good: a member who waved
// it away in the queue may well want it a fortnight later. The plain button on the
// profile page is never hidden, so the way in is always there.
const SNOOZE_KEY = 'sc:install-banner-snoozed-at'
const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000

function isBannerSnoozed(): boolean {
  try {
    const at = Number(localStorage.getItem(SNOOZE_KEY))
    return Number.isFinite(at) && at > 0 && Date.now() - at < SNOOZE_MS
  } catch {
    return false
  }
}

function snoozeBanner(): void {
  try {
    localStorage.setItem(SNOOZE_KEY, String(Date.now()))
  } catch {
    // Storage unavailable: it stays hidden until the next page load, which is enough.
  }
}

interface InstallAppButtonProps {
  fullWidth?: boolean
  className?: string
  /**
   * `banner` is the dashboard advert: a full-width row that says what installing gets
   * you, with a solid Install button and a way to wave it off. `button` is the quiet
   * one for menus and settings.
   */
  variant?: 'button' | 'banner'
}

/**
 * "Install app" affordance, rendered only while the app is not already installed.
 *
 * Android and desktop Chrome get the native install prompt captured by `usePwaInstall`.
 * iOS never fires `beforeinstallprompt`, so it gets a modal with the Add to Home Screen
 * steps plus a Learn more section for members who cannot find the Share button.
 *
 * Android gets the same modal, with its own steps, whenever the native prompt cannot be
 * shown: the browser never offered one (Firefox), it was already shown and dismissed, or
 * `prompt()` failed. Without that fallback the button is visible and a tap does nothing.
 *
 * Every tap that cannot show the native prompt goes through `install()` even when we
 * already know there is no prompt, because that is where the failure is reported. iOS is
 * the exception: having no prompt there is how the platform works, not a problem.
 *
 * The button shakes every few seconds so members notice it. The animation lives on a
 * wrapper rather than on the Button itself: HeroUI already drives the Button's
 * `transform` for its pressed state, and an infinite animation on the same element
 * would own that property permanently and swallow the press feedback. `className` is
 * layout, so it goes on the wrapper too.
 */
export function InstallAppButton({
  fullWidth = false,
  className,
  variant = 'button',
}: InstallAppButtonProps) {
  const { canInstall, canShowIosHint, isIos, isAndroid, isStandalone, install } = usePwaInstall()
  const { t } = useTranslation()
  const [stepsOpen, setStepsOpen] = useState(false)
  const [snoozed, setSnoozed] = useState(isBannerSnoozed)

  const userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent
  const browser = isIos ? iosBrowserName(userAgent) : androidBrowserName(userAgent)
  const isSamsung = browser === 'Samsung Internet'
  const canShowAndroidSteps = isAndroid && !isStandalone

  if (!canInstall && !canShowIosHint && !canShowAndroidSteps) return null

  const handlePress = async () => {
    if (!canInstall && isIos) {
      setStepsOpen(true)
      return
    }
    const outcome = await install()
    if (outcome === 'unavailable' && canShowAndroidSteps) setStepsOpen(true)
  }

  return (
    <>
      {variant === 'banner' ? (
        !snoozed && (
          <div
            className={`flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/10 p-3 ${className ?? ''}`}
          >
            {/* The icon installs too: it is the thing that moves, so it is what
                a thumb goes for. Hidden from assistive tech and the tab order,
                because the Install button beside it is the same action and
                nobody needs to hear it twice. The nudge stays on the wrapper
                for the reason given above. */}
            <span className="shrink-0 motion-safe:animate-nudge">
              <Button
                isIconOnly
                color="primary"
                className="h-10 w-10 min-w-10 rounded-lg"
                aria-hidden="true"
                excludeFromTabOrder
                data-testid="install-banner-icon"
                onPress={() => void handlePress()}
              >
                <DownloadIcon size={18} />
              </Button>
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold">{t('common.installBannerTitle')}</p>
              <p className="text-xs text-foreground/60">{t('common.installBannerBody')}</p>
            </div>
            <Button
              size="sm"
              color="primary"
              className="shrink-0"
              onPress={() => void handlePress()}
            >
              {t('common.installBannerAction')}
            </Button>
            <Button
              isIconOnly
              size="sm"
              variant="light"
              className="-ml-2 shrink-0 text-foreground/50"
              aria-label={t('common.installBannerDismiss')}
              onPress={() => {
                snoozeBanner()
                setSnoozed(true)
              }}
            >
              <CloseIcon />
            </Button>
          </div>
        )
      ) : (
        <span
          className={`${fullWidth ? 'block w-full' : 'inline-block'} motion-safe:animate-nudge ${className ?? ''}`}
        >
          <Button
            size="sm"
            variant="flat"
            color="primary"
            fullWidth={fullWidth}
            startContent={<DownloadIcon />}
            onPress={() => void handlePress()}
          >
            {t('common.installApp')}
          </Button>
        </span>
      )}

      <Modal
        isOpen={stepsOpen}
        onOpenChange={(open) => {
          if (!open) setStepsOpen(false)
        }}
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader>{t('common.installAppIosTitle')}</ModalHeader>
              <ModalBody>
                <p className="text-sm text-foreground/70">
                  {t('common.installAppIosIntro', { browser })}
                </p>
                {isIos ? (
                  <ol className="flex list-decimal flex-col gap-2 pl-5 text-sm">
                    <li>
                      {t('common.installAppIosStep1', { browser })}{' '}
                      <ShareIcon className="ml-0.5 inline align-middle" />
                    </li>
                    <li>{t('common.installAppIosStep2')}</li>
                    <li>{t('common.installAppIosStep3')}</li>
                  </ol>
                ) : (
                  <ol className="flex list-decimal flex-col gap-2 pl-5 text-sm">
                    <li>{t('common.installAppAndroidStep1', { browser })}</li>
                    <li>
                      {isSamsung
                        ? t('common.installAppAndroidStep2Samsung')
                        : t('common.installAppAndroidStep2')}
                    </li>
                    <li>{t('common.installAppAndroidStep3')}</li>
                  </ol>
                )}
                <Accordion isCompact className="px-0">
                  <AccordionItem
                    key="details"
                    aria-label={t('common.installAppLearnMore')}
                    title={<span className="text-sm">{t('common.installAppLearnMore')}</span>}
                    indicator={({ isOpen }) => <ChevronIcon isOpen={isOpen ?? false} />}
                  >
                    {isIos ? (
                      <div className="flex flex-col gap-2 pb-1 text-sm text-foreground/70">
                        <p>
                          {browser === 'Safari'
                            ? t('common.installAppIosDetailShareSafari')
                            : t('common.installAppIosDetailShareOther', { browser })}
                        </p>
                        <p>{t('common.installAppIosDetailSheet')}</p>
                        <p>{t('common.installAppIosDetailBrowsers')}</p>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2 pb-1 text-sm text-foreground/70">
                        <p>
                          {isSamsung
                            ? t('common.installAppAndroidDetailMenuSamsung')
                            : t('common.installAppAndroidDetailMenu')}
                        </p>
                        <p>{t('common.installAppAndroidDetailResult')}</p>
                      </div>
                    )}
                  </AccordionItem>
                </Accordion>
              </ModalBody>
              <ModalFooter>
                <Button color="primary" onPress={() => setStepsOpen(false)}>
                  {t('common.installAppIosDone')}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  )
}
